import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, Download, Mail, Phone, MapPin, CalendarDays, KeyRound, GraduationCap, Loader2, Send } from "lucide-react";
import { Paginacao, usePaginas } from "@/components/Paginacao";

const fmt = (iso) => (iso ? new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("pt-BR") : "");
const quando = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "hoje" : d === 1 ? "ontem" : d < 30 ? `há ${d} dias` : d < 60 ? "há 1 mês" : d < 365 ? `há ${Math.floor(d / 30)} meses` : fmt(iso);
};
const waLink = (tel, texto = "") => {
  let d = String(tel || "").replace(/\D/g, "");
  if (d.length === 10 || d.length === 11) d = "55" + d;
  return d.length >= 12 ? `https://wa.me/${d}${texto ? `?text=${encodeURIComponent(texto)}` : ""}` : "";
};
const curto = (t) => t.replace(/^EaD\/?\s*-?\s*/i, "").replace(" - Cabeleireiro Científico Formulador", "").replace("Curso de Especialização ", "");

const SIT = {
  ativa: ["Ativa", "bg-green-50 text-green-700"], vencida: ["Vencida", "bg-amber-50 text-amber-700"],
  bloqueada: ["Bloqueada", "bg-red-50 text-red-700"], sem_curso: ["Sem matrícula", "bg-slate-100 text-slate-600"],
};
function Situacao({ a }) {
  const [t, c] = SIT[a.situacao] || SIT.sem_curso;
  const extra = a.situacao === "ativa" && a.ativa_ate && a.ativa_ate !== "9999" ? ` até ${fmt(a.ativa_ate)}` : a.situacao === "vencida" && a.vencida_em ? ` em ${fmt(a.vencida_em)}` : "";
  return (
    <div className="space-y-1">
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c}`}>{t}{extra}</span>
      {a.conta === "nao_ativou" && <div><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Não ativou a conta nova</span></div>}
    </div>
  );
}
const podeConvidar = (a) => a.situacao !== "ativa" || a.conta === "nao_ativou";
const convidadaRecente = (a) => a.convite && Date.now() - new Date(a.convite.at).getTime() < 30 * 86400000;

function mensagemPadrao(a, prof) {
  const primeiro = (a.name || "").split(" ")[0];
  return `Oi, ${primeiro}! Aqui é a ${(prof || "").split(" ")[0]}, sua professora do Método Cabeleireiro Científico Formulador 💚\n\n`
    + "Senti sua falta! A plataforma do IEP está nova (iepplataforma.com), com as aulas atualizadas, e eu queria muito ter você de volta.\n\n"
    + "Veja os cursos em: iepplataforma.com/cursos\n\nQualquer dúvida, é só me chamar!";
}

function Convite({ grupo, aluna, onClose, onEnviado }) {
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { if (aluna) setMsg(mensagemPadrao(aluna, user?.name)); }, [aluna, user?.name]);
  const enviar = async (canal) => {
    setBusy(canal);
    const janela = canal === "whatsapp" ? window.open("", "_blank") : null;   // abre já no clique (o celular bloqueia janela aberta depois)
    try {
      const r = await api.post(`/groups/${grupo}/alunas/${aluna.id}/convite`, { canal, mensagem: msg });
      if (canal === "whatsapp") { if (janela) janela.location.href = r.data.whatsapp_url; else window.location.href = r.data.whatsapp_url; }
      toast.success("WhatsApp aberto com o convite. É só tocar em enviar.");
      onEnviado(); onClose();
    } catch (e) { if (janela) janela.close(); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return (
    <Dialog open={!!aluna} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">💌 Convidar {aluna?.name?.split(" ")[0]} para voltar ao Método</DialogTitle>
          <DialogDescription>Abre o seu WhatsApp com o texto pronto (pode editar antes). A mesma aluna só pode ser convidada de novo depois de 30 dias.</DialogDescription>
        </DialogHeader>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={9} className="text-sm" />
        <div className="flex flex-wrap gap-2">
          {!waLink(aluna?.phone) && <p className="text-sm text-red-600">Esta aluna não tem telefone válido.</p>}
          <Button disabled={!!busy || !waLink(aluna?.phone)} onClick={() => enviar("whatsapp")} className="text-white" style={{ background: "#25D366" }}>
            {busy === "whatsapp" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Phone className="h-4 w-4 mr-1.5" />} Enviar pelo meu WhatsApp</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Ficha({ grupo, alunaId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!alunaId) return;
    setD(null);
    api.get(`/groups/${grupo}/alunas/${alunaId}`).then((r) => setD(r.data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [grupo, alunaId]); // eslint-disable-line react-hooks/exhaustive-deps
  const a = d?.aluna;
  return (
    <Dialog open={!!alunaId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {!d ? <div className="grid place-items-center h-40"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (<>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{a.name}</DialogTitle>
            <DialogDescription>{a.reg_code ? `Matrícula ${a.reg_code} · ` : ""}só consulta</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-[1fr_1.5fr] gap-5">
            <div className="space-y-2 text-sm text-slate-700">
              {a.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {a.phone}
                {waLink(a.phone) && <a href={waLink(a.phone)} target="_blank" rel="noopener" className="text-xs font-semibold text-white rounded px-2 py-0.5" style={{ background: "#25D366" }}>WhatsApp</a>}</div>}
              {a.email && <div className="flex items-center gap-2 break-all"><Mail className="h-4 w-4 text-slate-400 shrink-0" /> {a.email}</div>}
              {(a.city || a.state) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {[a.city, a.state].filter(Boolean).join("/")}</div>}
              {a.desde && <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" /> Aluna desde {fmt(a.desde)}</div>}
              <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-slate-400" />
                {a.conta === "ativa" ? <>Conta <b className="text-green-700">ativa</b>{a.ultimo_acesso ? ` · último acesso ${quando(a.ultimo_acesso)}` : ""}</>
                  : <>Ainda não ativou a conta nova{a.ultimo_acesso_antiga ? ` · último acesso na antiga ${fmt(a.ultimo_acesso_antiga)}` : ""}</>}</div>
              <p className="text-xs text-slate-400 border-t pt-2">Não aparecem: CPF, nascimento, endereço completo e pagamentos.</p>
            </div>
            <div className="space-y-4">
              {d.cursos.length === 0 && <p className="text-sm text-slate-500">Sem matrícula em curso.</p>}
              {d.cursos.map((c) => (
                <div key={c.id}>
                  <div className="flex justify-between gap-2 text-sm"><b className="text-slate-800">{c.title}</b>
                    <span className={`text-xs font-semibold shrink-0 ${c.estado === "ativa" ? "text-green-700" : c.estado === "bloqueada" ? "text-red-700" : "text-amber-700"}`}>
                      {c.estado === "ativa" ? (c.expires_at ? `ativa até ${fmt(c.expires_at)}` : "ativa") : c.estado === "expirada" ? `vencida em ${fmt(c.expires_at)}` : c.estado}</span></div>
                  <div className="h-2 bg-slate-100 rounded mt-1.5"><div className="h-2 rounded bg-[#1E9E2A]" style={{ width: `${c.pct}%` }} /></div>
                  <div className="text-xs text-slate-500 mt-1">{c.feitas} de {c.total} aulas · {c.pct}%{c.horas ? ` · ${c.horas} h assistidas` : ""}{c.parou_em ? ` · parou em "${c.parou_em}"` : ""}
                    {c.prova ? ` · prova: ${c.prova.aprovada ? "aprovada" : "não aprovada"} (melhor ${Math.round(c.prova.melhor)}%)` : ""}
                    {c.progresso_antigo ? ` · na plataforma antiga: ${c.progresso_antigo}` : ""}</div>
                </div>
              ))}
              <div className="text-sm text-slate-600 border-t pt-3 flex items-start gap-2"><GraduationCap className="h-4 w-4 mt-0.5 text-slate-400" />
                {d.certificados.length ? <div>{d.certificados.map((c) => <div key={c.codigo}>{c.curso} · {fmt(c.emitido_em)}</div>)}</div> : "Certificados: nenhum ainda"}</div>
            </div>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

/** Lista de alunas da turma para a professora: só consulta + convite para voltar. */
export default function MinhasAlunas({ grupo }) {
  const [d, setD] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [q, setQ] = useState("");
  const [ficha, setFicha] = useState(null);
  const [convite, setConvite] = useState(null);
  const carregar = () => api.get(`/groups/${grupo}/alunas`).then((r) => setD(r.data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setD({ alunas: [], contagem: {} }); });
  useEffect(() => { carregar(); }, [grupo]); // eslint-disable-line react-hooks/exhaustive-deps

  const lista = useMemo(() => {
    if (!d) return [];
    const norm = (t) => (t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const t = norm(q.trim()); const dig = q.replace(/\D/g, "");
    return d.alunas.filter((a) => (filtro === "todas" || (filtro === "nao_ativou" ? a.conta === "nao_ativou" : a.situacao === filtro))
      && (!t || norm(`${a.name} ${a.email} ${a.city}`).includes(t) || (dig.length >= 4 && (a.phone || "").replace(/\D/g, "").includes(dig))));
  }, [d, filtro, q]);
  const pag = usePaginas(lista, 25);

  const baixar = () => {
    const cab = ["Nome", "Telefone", "E-mail", "Cidade", "UF", "Situação", "Conta", "Cursos", "Último acesso"];
    const linhas = lista.map((a) => [a.name, a.phone, a.email, a.city, a.state, (SIT[a.situacao] || [""])[0], a.conta === "ativa" ? "ativa" : "não ativou",
      a.cursos.map((c) => `${curto(c.title)} ${c.pct}%`).join(" | "), a.ultimo_acesso ? fmt(a.ultimo_acesso) : a.ultimo_acesso_antiga ? `${fmt(a.ultimo_acesso_antiga)} (antiga)` : ""]);
    const csv = [cab, ...linhas].map((l) => l.map((x) => `"${String(x || "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const el = document.createElement("a"); el.href = url; el.download = `alunas-${(d.group?.name || "turma").replace(/\W+/g, "-")}.csv`; el.click(); URL.revokeObjectURL(url);
  };

  if (!d) return <Skeleton className="h-64 rounded-xl" />;
  const c = d.contagem || {};
  const chips = [["todas", `Todas ${c.todas || 0}`], ["ativa", `✅ Ativas ${c.ativa || 0}`], ["vencida", `⏰ Vencidas ${c.vencida || 0}`],
    ["bloqueada", `⛔ Bloqueadas ${c.bloqueada || 0}`], ["nao_ativou", `💤 Não ativaram a conta ${c.nao_ativou || 0}`], ...(c.sem_curso ? [["sem_curso", `Sem matrícula ${c.sem_curso}`]] : [])];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="minhas-alunas">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><Users className="h-4 w-4" /> {d.acesso === "consulta" ? "Alunas da turma" : "Minhas alunas"} ({c.todas || 0})</h2>
          <p className="text-xs text-slate-400 mt-0.5">Clique na aluna para ver os dados e o progresso de cada curso. Só consulta: alterações são feitas pelo IEP.</p>
        </div>
        <Button variant="outline" size="sm" onClick={baixar}><Download className="h-4 w-4 mr-1.5" /> Baixar lista</Button>
      </div>
      <div className="px-5 pt-4 flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); pag.setPagina(1); }} placeholder="Nome, e-mail, telefone ou cidade" className="pl-9 h-10" data-testid="minhas-alunas-busca" />
        </div>
        {chips.map(([k, t]) => (
          <button key={k} onClick={() => { setFiltro(k); pag.setPagina(1); }}
            className={`px-3 py-1.5 rounded-full text-sm border ${filtro === k ? "bg-[#232D4F] text-white border-[#232D4F]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>
        ))}
      </div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
            <th className="px-5 py-3">Aluna</th><th className="px-3 py-3">Contato</th><th className="px-3 py-3">Situação</th>
            <th className="px-3 py-3">Cursos e progresso</th><th className="px-3 py-3">Último acesso</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {pag.itens.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50/60 align-top">
                <td className="px-5 py-3 cursor-pointer" onClick={() => setFicha(a.id)}>
                  <div className="font-semibold text-slate-800 hover:underline">{a.name}</div>
                  <div className="text-xs text-slate-400">{[a.city, a.state].filter(Boolean).join("/")}</div>
                </td>
                <td className="px-3 py-3">
                  {a.phone && <div className="text-slate-700 whitespace-nowrap">{a.phone}</div>}
                  <div className="text-xs text-slate-400 break-all">{a.email}</div>
                  {waLink(a.phone) && <a href={waLink(a.phone)} target="_blank" rel="noopener" className="inline-block mt-1 text-xs font-semibold text-white rounded px-2 py-0.5" style={{ background: "#25D366" }}>WhatsApp</a>}
                </td>
                <td className="px-3 py-3">
                  <Situacao a={a} />
                  {podeConvidar(a) && (convidadaRecente(a)
                    ? <div className="text-[11px] text-slate-400 mt-1.5">💌 convidada {quando(a.convite.at)}{a.convite.por ? ` por ${a.convite.por.split(" ")[0]}` : ""}</div>
                    : <button onClick={() => setConvite(a)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-white rounded px-2 py-1" style={{ background: "#C9A06A" }}><Send className="h-3 w-3" /> Convidar para voltar</button>)}
                </td>
                <td className="px-3 py-3 w-64 cursor-pointer" onClick={() => setFicha(a.id)}>
                  {a.cursos.length === 0 ? <span className="text-xs text-slate-400">—</span> : a.cursos.map((c2) => (
                    <div key={c2.id} className="mb-1.5">
                      <div className="text-xs text-slate-600">{curto(c2.title)} <b>{c2.pct}%</b>{c2.certificado ? " · 🎓" : ""}</div>
                      <div className="h-1.5 bg-slate-100 rounded"><div className={`h-1.5 rounded ${c2.estado === "ativa" ? "bg-[#1E9E2A]" : "bg-amber-400"}`} style={{ width: `${c2.pct}%` }} /></div>
                    </div>
                  ))}
                </td>
                <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{a.ultimo_acesso ? quando(a.ultimo_acesso)
                  : a.ultimo_acesso_antiga ? <>{fmt(a.ultimo_acesso_antiga)} <div className="text-[11px] text-slate-400">plataforma antiga</div></> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3">
        {lista.length === 0 ? <span className="text-xs text-slate-400">Nenhuma aluna neste filtro.</span>
          : pag.paginas > 1 ? <Paginacao {...pag} rotulo="alunas" subir={false} />
          : <span className="text-xs text-slate-400">{lista.length} aluna(s)</span>}
      </div>
      <Ficha grupo={grupo} alunaId={ficha} onClose={() => setFicha(null)} />
      <Convite grupo={grupo} aluna={convite} onClose={() => setConvite(null)} onEnviado={carregar} />
    </div>
  );
}
